import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatError } from "../lib/api";
import { toast } from "sonner";
import { Wine } from "lucide-react";

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const [form, setForm] = useState({ email: "", password: "" });
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const user = await login(form.email, form.password);
            toast.success("Welcome back.");
            const redirect = params.get("redirect");
            if (redirect) navigate(redirect);
            else if (user.role === "admin") navigate("/admin");
            else if (user.role === "winery") navigate("/dashboard");
            else navigate("/");
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-black text-white grid md:grid-cols-2">
            <div className="hidden md:block relative">
                <img
                    src="https://images.unsplash.com/photo-1765850257541-b70e125323d3?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80"
                    alt="vineyard"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/50" />
                <Link to="/" className="absolute top-8 left-8 flex items-center gap-2">
                    <Wine className="w-5 h-5 text-[#C9A961]" strokeWidth={1.5} />
                    <span className="font-display text-2xl">CellarGlobal</span>
                </Link>
                <div className="absolute bottom-12 left-8 right-8">
                    <p className="font-display-italic text-3xl text-[#C9A961]">"Wine is poetry in a bottle."</p>
                </div>
            </div>

            <div className="flex items-center justify-center px-8 py-16">
                <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
                    <Link to="/" className="md:hidden flex items-center gap-2 mb-12">
                        <Wine className="w-5 h-5 text-[#C9A961]" strokeWidth={1.5} />
                        <span className="font-display text-2xl">CellarGlobal</span>
                    </Link>
                    <p className="overline">Sign In</p>
                    <h1 className="font-display text-5xl mt-3 mb-12">Welcome back.</h1>

                    <div className="space-y-8">
                        <div>
                            <label className="overline block mb-2">Email</label>
                            <input required type="email" className="input-line" data-testid="login-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                        </div>
                        <div>
                            <label className="overline block mb-2">Password</label>
                            <input required type="password" className="input-line" data-testid="login-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                        </div>
                    </div>

                    <button type="submit" disabled={loading} className="btn-gold w-full mt-12" data-testid="login-submit">
                        {loading ? "Signing in..." : "Sign In"}
                    </button>

                    <p className="text-sm text-[#A1A1AA] mt-8 text-center">
                        New to CellarGlobal? <Link to="/register" className="text-[#C9A961] hover:underline">Create an account</Link>
                    </p>
                </form>
            </div>
        </div>
    );
}
